import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Pencil, Trash2, Plus, X, LogOut, Package, Tag, Mail, Instagram } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY_PRODUCT = {
  name: "", description: "", price: "", category: "", image_url: "",
  external_url: "",
  sizes: "S,M,L,XL", stock: 25, featured: false,
};
const EMPTY_DISCOUNT = { code: "", percent_off: 10, min_subtotal: 0, active: true };
const EMPTY_SOCIAL = { image_url: "", caption: "", link_url: "https://www.instagram.com/prophetofluv", order: 0 };

export default function AdminDashboard() {
  const { isAuthed, logout, email } = useAuth();
  const [tab, setTab] = useState("products");

  if (!isAuthed) return <Navigate to="/admin/login" replace />;

  return (
    <div className="pt-28 pb-20 min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-wrap justify-between items-end gap-6 mb-8 border-b border-white/10 pb-8">
          <div>
            <p className="overline mb-3">Studio Console</p>
            <h1 className="font-display text-5xl text-white tracking-tight">Workshop</h1>
            <p className="text-white/60 mt-2 text-sm">Signed in as {email}</p>
          </div>
          <button onClick={logout} className="btn-outline-gold" data-testid="logout-btn"><LogOut size={14}/> Logout</button>
        </div>

        <div className="flex gap-2 mb-10 flex-wrap" data-testid="admin-tabs">
          <TabBtn active={tab==="products"} onClick={()=>setTab("products")} icon={<Package size={14}/>} label="Products" testid="tab-products"/>
          <TabBtn active={tab==="discounts"} onClick={()=>setTab("discounts")} icon={<Tag size={14}/>} label="Discount Codes" testid="tab-discounts"/>
          <TabBtn active={tab==="newsletter"} onClick={()=>setTab("newsletter")} icon={<Mail size={14}/>} label="Newsletter" testid="tab-newsletter"/>
          <TabBtn active={tab==="social"} onClick={()=>setTab("social")} icon={<Instagram size={14}/>} label="Social Wall" testid="tab-social"/>
        </div>

        {tab === "products" && <ProductsPanel />}
        {tab === "discounts" && <DiscountsPanel />}
        {tab === "newsletter" && <NewsletterPanel />}
        {tab === "social" && <SocialPanel />}
      </div>
      <style>{`.ip{background:transparent;border:1px solid rgba(255,255,255,0.2);padding:.65rem .85rem;color:#fff;width:100%;outline:none;transition:border-color .2s}.ip:focus{border-color:var(--davide-gold)}`}</style>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-6 py-3 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2 transition-all ${
        active ? "bg-[var(--davide-gold)] text-black" : "border border-white/20 text-white/70 hover:border-[var(--davide-gold)] hover:text-[var(--davide-gold)]"
      }`}>
      {icon}{label}
    </button>
  );
}

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
      <label className="overline block mb-2" style={{color:"var(--davide-muted)"}}>{label}</label>
      {children}
    </div>
  );
}

function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_PRODUCT);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(EMPTY_PRODUCT); setShowForm(true); };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p, price: p.price.toString(), sizes: (p.sizes || []).join(",") });
    setShowForm(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, description: form.description,
      price: parseFloat(form.price), category: form.category,
      image_url: form.image_url,
      external_url: form.external_url || null,
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      stock: parseInt(form.stock) || 0,
      featured: !!form.featured,
    };
    try {
      if (editing) { await api.put(`/admin/products/${editing}`, payload); toast.success("Updated"); }
      else { await api.post("/admin/products", payload); toast.success("Created"); }
      setShowForm(false); setForm(EMPTY_PRODUCT); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-3xl text-white">Product Catalog</h2>
        <button onClick={startNew} className="btn-gold" data-testid="add-product-btn"><Plus size={16}/> New Piece</button>
      </div>

      {showForm && (
        <div className="border border-white/10 bg-[#101010] p-8 mb-10" data-testid="product-form">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-display text-2xl text-white">{editing ? "Edit Piece" : "New Piece"}</h2>
            <button onClick={() => setShowForm(false)} className="text-white/50 hover:text-white" data-testid="close-form-btn"><X size={20}/></button>
          </div>
          <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-5">
            <Field label="Name" testid="form-name"><input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="ip"/></Field>
            <Field label="Category" testid="form-category"><input required value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} className="ip"/></Field>
            <Field label="Price (USD)" testid="form-price"><input required type="number" step="0.01" value={form.price} onChange={(e)=>setForm({...form,price:e.target.value})} className="ip"/></Field>
            <Field label="Stock" testid="form-stock"><input type="number" value={form.stock} onChange={(e)=>setForm({...form,stock:e.target.value})} className="ip"/></Field>
            <Field label="Image URL" testid="form-image" full><input required value={form.image_url} onChange={(e)=>setForm({...form,image_url:e.target.value})} className="ip"/></Field>
            <Field label="External Store URL (Wix / AliveShoes — optional, for fulfilment)" testid="form-external-url" full>
              <input value={form.external_url || ""} placeholder="https://www.rachvidwearsusa.com/product-page/..."
                onChange={(e)=>setForm({...form,external_url:e.target.value})} className="ip"/>
            </Field>
            <Field label="Sizes (comma-separated)" testid="form-sizes" full><input value={form.sizes} onChange={(e)=>setForm({...form,sizes:e.target.value})} className="ip"/></Field>
            <Field label="Description" testid="form-description" full><textarea required rows={4} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className="ip"/></Field>
            <label className="flex items-center gap-3 text-sm text-white/80 md:col-span-2 cursor-pointer" data-testid="form-featured">
              <input type="checkbox" checked={form.featured} onChange={(e)=>setForm({...form,featured:e.target.checked})} className="accent-[var(--davide-gold)] w-4 h-4"/>
              Feature on homepage
            </label>
            <div className="md:col-span-2 flex gap-3 pt-2">
              <button type="submit" className="btn-gold" data-testid="save-product-btn">{editing ? "Update" : "Create"}</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline-gold">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-sm" data-testid="products-table">
          <thead className="bg-[#0E0E0E] text-white/60 uppercase tracking-widest text-xs">
            <tr>
              <th className="text-left px-5 py-4">Image</th>
              <th className="text-left px-5 py-4">Name</th>
              <th className="text-left px-5 py-4">Category</th>
              <th className="text-left px-5 py-4">Price</th>
              <th className="text-left px-5 py-4">Featured</th>
              <th className="text-right px-5 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-[#0E0E0E]" data-testid={`product-row-${p.id}`}>
                <td className="px-5 py-3"><img src={p.image_url} alt="" className="w-12 h-16 object-cover"/></td>
                <td className="px-5 py-3 text-white">{p.name}</td>
                <td className="px-5 py-3 text-white/70">{p.category}</td>
                <td className="px-5 py-3 text-[var(--davide-gold)]">${p.price.toFixed(2)}</td>
                <td className="px-5 py-3 text-white/70">{p.featured ? "★" : "—"}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => startEdit(p)} className="text-white/70 hover:text-[var(--davide-gold)] mr-3" data-testid={`edit-${p.id}-btn`}><Pencil size={16}/></button>
                  <button onClick={() => onDelete(p)} className="text-white/70 hover:text-[var(--davide-red)]" data-testid={`delete-${p.id}-btn`}><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DiscountsPanel() {
  const [codes, setCodes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_DISCOUNT);

  const load = () => api.get("/admin/discounts").then((r) => setCodes(r.data));
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/discounts", {
        code: form.code.toUpperCase().trim(),
        percent_off: parseFloat(form.percent_off),
        min_subtotal: parseFloat(form.min_subtotal || 0),
        active: !!form.active,
      });
      toast.success("Code created");
      setShowForm(false); setForm(EMPTY_DISCOUNT); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const onDelete = async (d) => {
    if (!window.confirm(`Delete code "${d.code}"?`)) return;
    await api.delete(`/admin/discounts/${d.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display text-3xl text-white">Discount Codes</h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-gold" data-testid="add-discount-btn">
          <Plus size={16}/> New Code
        </button>
      </div>

      {showForm && (
        <div className="border border-white/10 bg-[#101010] p-8 mb-10" data-testid="discount-form">
          <form onSubmit={onSubmit} className="grid md:grid-cols-3 gap-5">
            <Field label="Code" testid="d-form-code">
              <input required value={form.code} onChange={(e)=>setForm({...form,code:e.target.value.toUpperCase()})}
                placeholder="SUMMER20" className="ip uppercase tracking-widest"/>
            </Field>
            <Field label="% Off" testid="d-form-percent">
              <input required type="number" min="1" max="90" value={form.percent_off}
                onChange={(e)=>setForm({...form,percent_off:e.target.value})} className="ip"/>
            </Field>
            <Field label="Min. Subtotal ($)" testid="d-form-min">
              <input type="number" min="0" step="0.01" value={form.min_subtotal}
                onChange={(e)=>setForm({...form,min_subtotal:e.target.value})} className="ip"/>
            </Field>
            <label className="flex items-center gap-3 text-sm text-white/80 md:col-span-3 cursor-pointer" data-testid="d-form-active">
              <input type="checkbox" checked={form.active} onChange={(e)=>setForm({...form,active:e.target.checked})}
                className="accent-[var(--davide-gold)] w-4 h-4"/>
              Active (customers can use it now)
            </label>
            <div className="md:col-span-3 flex gap-3">
              <button type="submit" className="btn-gold" data-testid="save-discount-btn">Create</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline-gold">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-sm" data-testid="discounts-table">
          <thead className="bg-[#0E0E0E] text-white/60 uppercase tracking-widest text-xs">
            <tr>
              <th className="text-left px-5 py-4">Code</th>
              <th className="text-left px-5 py-4">% Off</th>
              <th className="text-left px-5 py-4">Min. Subtotal</th>
              <th className="text-left px-5 py-4">Status</th>
              <th className="text-right px-5 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-white/50 py-10">No codes yet.</td></tr>
            ) : codes.map((d) => (
              <tr key={d.id} className="border-t border-white/5 hover:bg-[#0E0E0E]" data-testid={`discount-row-${d.id}`}>
                <td className="px-5 py-3 text-[var(--davide-gold)] font-medium tracking-widest">{d.code}</td>
                <td className="px-5 py-3 text-white">{d.percent_off}%</td>
                <td className="px-5 py-3 text-white/70">${Number(d.min_subtotal || 0).toFixed(2)}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${d.active ? "text-[var(--davide-gold)] bg-[var(--davide-gold)]/10" : "text-white/40 bg-white/5"}`}>
                    {d.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => onDelete(d)} className="text-white/70 hover:text-[var(--davide-red)]" data-testid={`delete-discount-${d.id}-btn`}><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function NewsletterPanel() {
  const [data, setData] = useState({ count: 0, subscribers: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/newsletter").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const exportCsv = () => {
    const csv = "email,source,created_at\n" + data.subscribers.map(s =>
      `${s.email},${s.source || ""},${s.created_at || ""}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `davide-newsletter-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-white/50 py-10 text-center">Loading…</div>;

  return (
    <div data-testid="newsletter-panel">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display text-3xl text-white">Newsletter Subscribers</h2>
          <p className="text-white/60 text-sm mt-1"><span className="text-[var(--davide-gold)] font-medium">{data.count}</span> people on the list.</p>
        </div>
        {data.count > 0 && (
          <button onClick={exportCsv} className="btn-outline-gold" data-testid="export-csv-btn">Export CSV</button>
        )}
      </div>
      <div className="overflow-x-auto border border-white/10">
        <table className="w-full text-sm" data-testid="subscribers-table">
          <thead className="bg-[#0E0E0E] text-white/60 uppercase tracking-widest text-xs">
            <tr>
              <th className="text-left px-5 py-4">Email</th>
              <th className="text-left px-5 py-4">Source</th>
              <th className="text-left px-5 py-4">Subscribed</th>
            </tr>
          </thead>
          <tbody>
            {data.subscribers.length === 0 ? (
              <tr><td colSpan={3} className="text-center text-white/50 py-10">No subscribers yet.</td></tr>
            ) : data.subscribers.map((s) => (
              <tr key={s.id} className="border-t border-white/5 hover:bg-[#0E0E0E]" data-testid={`subscriber-row-${s.id}`}>
                <td className="px-5 py-3 text-white">{s.email}</td>
                <td className="px-5 py-3 text-white/70 uppercase tracking-widest text-xs">{s.source || "—"}</td>
                <td className="px-5 py-3 text-white/60 text-xs">{new Date(s.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SocialPanel() {
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_SOCIAL);
  const load = () => api.get("/social").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/social", {
        image_url: form.image_url,
        caption: form.caption || "",
        link_url: form.link_url || null,
        order: parseInt(form.order) || 0,
      });
      toast.success("Post added");
      setShowForm(false); setForm(EMPTY_SOCIAL); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const onDelete = async (p) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/social/${p.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="social-panel">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display text-3xl text-white">Social Wall</h2>
          <p className="text-white/60 text-sm mt-1">These 6 images appear on the homepage grid.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-gold" data-testid="add-social-btn">
          <Plus size={16}/> New Post
        </button>
      </div>

      {showForm && (
        <div className="border border-white/10 bg-[#101010] p-8 mb-10" data-testid="social-form">
          <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-5">
            <Field label="Image URL" testid="s-form-image" full>
              <input required value={form.image_url}
                onChange={(e)=>setForm({...form,image_url:e.target.value})} className="ip"/>
            </Field>
            <Field label="Caption (optional)" testid="s-form-caption">
              <input value={form.caption}
                onChange={(e)=>setForm({...form,caption:e.target.value})} className="ip"/>
            </Field>
            <Field label="Link URL (default: Instagram)" testid="s-form-link">
              <input value={form.link_url}
                onChange={(e)=>setForm({...form,link_url:e.target.value})} className="ip"/>
            </Field>
            <Field label="Sort Order (0 = first)" testid="s-form-order">
              <input type="number" value={form.order}
                onChange={(e)=>setForm({...form,order:e.target.value})} className="ip"/>
            </Field>
            <div className="md:col-span-2 flex gap-3">
              <button type="submit" className="btn-gold" data-testid="save-social-btn">Create</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline-gold">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="social-grid">
        {posts.length === 0 ? (
          <p className="text-white/50 col-span-3 text-center py-10">No posts yet.</p>
        ) : posts.map((p) => (
          <div key={p.id} className="relative group border border-white/10 aspect-square overflow-hidden" data-testid={`social-post-row-${p.id}`}>
            <img src={p.image_url} alt="" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-4">
              <p className="text-white text-sm text-center">{p.caption || "—"}</p>
              <button onClick={() => onDelete(p)} className="text-[var(--davide-red)] hover:text-white" data-testid={`delete-social-${p.id}-btn`}><Trash2 size={20}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
